import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export type DocumentType = 'blog' | 'document';

export interface DocumentRecord {
  /** Stable id (the ai_messages.id that holds this artifact). */
  id: string;
  /** Session the artifact was generated in — powers "Open in chat" deep-link. */
  sessionId: string;
  /** Tool args: title. May be empty; renderer supplies "Untitled blog/document" fallback. */
  title: string;
  /** Tool args: type. One of the `ARTIFACT_TYPES` from generate-document.ts. */
  type: DocumentType | string;
  /** Markdown body. Kept in-memory only when canvas is open — list view uses snippet. */
  markdown: string;
  /** First ~80 chars of markdown, stripped of heading markers. */
  snippet: string;
  /** Word count derived once at list-load time. */
  wordCount: number;
  /** The message's created_at — used for "Updated X ago" display and sort. */
  createdAt: string;
}

interface ToolGenerateDocumentPart {
  type: 'tool-generate_document';
  state: 'output-available';
  output?: {
    id?: string;
    title?: string;
    type?: string;
    markdown?: string;
  };
}

interface AiMessageRow {
  id: string;
  session_id: string;
  created_at: string;
  parts: Array<{ type?: string; state?: string; output?: unknown }> | null;
}

function stripHeadingMarkers(markdown: string): string {
  return markdown
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*_]{3,}$/gm, '')
    .replace(/\*\*|__|~~|\*|_|`/g, '')
    .trim();
}

function buildSnippet(markdown: string, maxChars = 80): string {
  const clean = stripHeadingMarkers(markdown).replace(/\s+/g, ' ');
  return clean.length > maxChars ? `${clean.slice(0, maxChars).trim()}…` : clean;
}

function countWords(markdown: string): number {
  const clean = stripHeadingMarkers(markdown);
  if (!clean) return 0;
  return clean.split(/\s+/).filter(Boolean).length;
}

const AI_MESSAGE_PK_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A chat UIMessage id is NOT always `ai_messages.id` (the uuid PK that
 * `.eq('id', …)` lookups and the `save_document_markdown`/`p_message uuid`
 * RPC require). It can be:
 *  - `ai_messages.message_id` — the AI SDK's own generated id, e.g.
 *    `msg-7GuSlOwEQqt1OTFC` (`createIdGenerator({ prefix: 'msg', size: 16 })`
 *    in `server/api/agent/index.post.ts`) — NOT a uuid.
 *  - `msg-<uuid>` / `usr-<uuid>` — the reload-time fallback synthesized in
 *    `server/api/agent/[sessionId]/messages/index.get.ts:158-163` for a row
 *    whose `message_id` is null/empty. Here `<uuid>` IS `row.id`, the PK.
 *  - a bare uuid — the PK itself, as passed by the list/library path
 *    (`listDocuments`'s `row.id`, `plan_documents.document_message_id`).
 *
 * Sending the wrong shape straight to a uuid column/param errors — this is
 * why every chat-generated document failed to reload
 * (`docs/planning/PLAN-campaign-entry-and-chat-integrity-2026-09-23.md` F1).
 * This tells the three shapes apart so callers can resolve the right one.
 */
export function classifyDocumentMessageIdentifier(identifier: string): {
  column: 'id' | 'message_id';
  value: string;
} {
  if (AI_MESSAGE_PK_UUID_RE.test(identifier)) return { column: 'id', value: identifier };
  const reloadFallback = identifier.match(/^(?:msg|usr)-(.+)$/);
  if (reloadFallback && AI_MESSAGE_PK_UUID_RE.test(reloadFallback[1])) {
    return { column: 'id', value: reloadFallback[1] };
  }
  return { column: 'message_id', value: identifier };
}

/**
 * Resolves any of the three id shapes `classifyDocumentMessageIdentifier`
 * recognizes to the `ai_messages.id` PK. Returns null (never throws) when a
 * `message_id` lookup finds no row or the query errors — a lookup miss and
 * "document not found" are the same outcome to every caller below.
 *
 * `message_id` is unique per `(session_id, message_id)`, NOT per
 * `(user_id, message_id)` — `branch_ai_session` (see
 * `supabase/migrations/20260831175940_chat_workspace_pin_inheritance.sql`)
 * copies a session's rows, `message_id` included, verbatim into the new
 * branch for the same user. So filtering on `(message_id, user_id)` alone can
 * legitimately match more than one row once a user branches a session that
 * passed through this document's message. A `.maybeSingle()` there throws
 * PGRST116 ("multiple (or no) rows returned") on the >1 case, which the old
 * `if (error || !data) return null` collapsed into an ordinary miss —
 * silently reporting "document not found" in BOTH the original and the
 * branched session. Order by `created_at DESC` and take the newest match
 * instead — the same deterministic tie-break `branch_ai_session` itself uses
 * — so ambiguity resolves to the most recently written copy rather than
 * erroring.
 */
async function resolveAiMessagePk(
  identifier: string,
  filters: { userId?: string; excludeDeleted?: boolean } = {},
): Promise<string | null> {
  const { column, value } = classifyDocumentMessageIdentifier(identifier);
  if (column === 'id') return value;
  let query = supabase.from('ai_messages').select('id').eq('message_id', value);
  if (filters.userId) query = query.eq('user_id', filters.userId);
  if (filters.excludeDeleted) query = query.is('deleted_at', null);
  const { data, error } = await query.order('created_at', { ascending: false }).limit(1);
  if (error || !data || data.length === 0) return null;
  return (data[0] as { id: string }).id;
}

function extractDocumentPart(
  parts: AiMessageRow['parts'],
): ToolGenerateDocumentPart['output'] | null {
  if (!parts) return null;
  for (const p of parts) {
    if (
      p &&
      typeof p === 'object' &&
      p.type === 'tool-generate_document' &&
      p.state === 'output-available' &&
      p.output &&
      typeof p.output === 'object'
    ) {
      return p.output as ToolGenerateDocumentPart['output'];
    }
  }
  return null;
}

/**
 * List all /blog and /document artifacts the user has generated.
 *
 * Source of truth: ai_messages.parts[]. No separate artifacts table.
 * JSONB `@>` containment powered by `ai_messages_parts_gin` index.
 *
 * RLS piggybacks on the existing ai_messages policy for the active account context.
 *
 * @param userId - authenticated user id
 * @param limit - max rows; default 50. Pagination beyond this adds a "Load more" button.
 */
export async function listDocuments(userId: string, limit = 50): Promise<DocumentRecord[]> {
  if (!userId) return [];
  // RLS on ai_messages scopes rows to the active account context. Avoid an
  // explicit actor-id filter here so shared-account members can see account
  // documents they are allowed to access.
  //
  // Use .filter('parts','cs', JSON.stringify(...)) instead of .contains() —
  // postgrest serializes object-array literals differently for jsonb vs array
  // columns; .contains() fails with "invalid input syntax for type json" on
  // jsonb columns with object-array payloads. The explicit string form is
  // what the GIN index (jsonb_path_ops) indexes against with @>.
  const containsFilter = JSON.stringify([
    { type: 'tool-generate_document', state: 'output-available' },
  ]);
  const { data, error } = await supabase
    .from('ai_messages')
    .select('id, session_id, created_at, parts')
    .filter('parts', 'cs', containsFilter)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.error('[DocumentsService.listDocuments]', error);
    throw new Error(error.message);
  }
  const rows = (data ?? []) as unknown as AiMessageRow[];
  const records: DocumentRecord[] = [];
  // P1.2 + P4.2 — soft dedup. Workflow runs (e.g. UGC + Bulk Repurpose) emit
  // the SAME generate_document output multiple times when the agent retries
  // or when the same workflow is run repeatedly against the same input. Each
  // emission becomes a new ai_messages row, so the library accumulates
  // visually-identical cards. We dedup on a (type|title|first-200-chars)
  // soft key. The most recent row wins (rows already sorted DESC by
  // created_at). Cheaper than SHA-256 + sufficient for this surface.
  const seenKeys = new Set<string>();
  for (const row of rows) {
    const output = extractDocumentPart(row.parts);
    if (!output || typeof output.markdown !== 'string') continue;
    const title =
      typeof output.title === 'string' && output.title.trim()
        ? output.title.trim()
        : output.type === 'blog'
          ? 'Untitled blog'
          : 'Untitled document';
    const docType = typeof output.type === 'string' ? output.type : 'document';
    const dedupKey = `${docType}|${title.toLowerCase()}|${output.markdown.slice(0, 200).trim().toLowerCase()}`;
    if (seenKeys.has(dedupKey)) continue;
    seenKeys.add(dedupKey);
    records.push({
      id: row.id,
      sessionId: row.session_id,
      title,
      type: docType,
      markdown: output.markdown,
      snippet: buildSnippet(output.markdown),
      wordCount: countWords(output.markdown),
      createdAt: row.created_at,
    });
  }
  return records;
}

/**
 * Single-fetch counterpart to `listDocuments` — used by Plan Detail and the
 * floating drawer when a row click resolves a known message id and we need
 * the full markdown body inline (no second navigation).
 *
 * Returns `null` when the row exists but doesn't contain a generate_document
 * part (e.g. the part was tombstoned via `deleteDocument`), so callers can
 * surface a friendly "this document is no longer available" state instead
 * of an indistinguishable error.
 *
 * @param messageId - either the `ai_messages.id` PK (the list/library path)
 *   or a chat UIMessage id (`ai_messages.message_id`, or its `msg-`/`usr-`
 *   reload fallback) — see `classifyDocumentMessageIdentifier` above.
 */
export async function getDocument(
  messageId: string,
  userId: string,
): Promise<DocumentRecord | null> {
  if (!messageId || !userId) return null;
  const pk = await resolveAiMessagePk(messageId, { userId });
  if (!pk) return null;
  const { data, error } = await supabase
    .from('ai_messages')
    .select('id, session_id, created_at, parts')
    .eq('id', pk)
    .maybeSingle();
  if (error) {
    console.error('[DocumentsService.getDocument]', error);
    return null;
  }
  if (!data) return null;
  const row = data as unknown as AiMessageRow;
  const output = extractDocumentPart(row.parts);
  if (!output || typeof output.markdown !== 'string') return null;
  const title =
    typeof output.title === 'string' && output.title.trim()
      ? output.title.trim()
      : output.type === 'blog'
        ? 'Untitled blog'
        : 'Untitled document';
  return {
    id: row.id,
    sessionId: row.session_id,
    title,
    type: typeof output.type === 'string' ? output.type : 'document',
    markdown: output.markdown,
    snippet: buildSnippet(output.markdown),
    wordCount: countWords(output.markdown),
    createdAt: row.created_at,
  };
}

/**
 * Soft-delete a document by clearing the tool-generate_document part from the ai_messages row.
 * Since our source of truth for documents is `ai_messages.parts[]`, we replace the document
 * part with a tombstone so the row remains readable in chat history but drops out of
 * `listDocuments()`.
 *
 * @param messageId - the ai_messages.id (maps to DocumentRecord.id)
 * @param userId - authenticated user id; RLS scopes the write to the active account context
 * @returns { ok: true } on success, { ok: false, error } on failure
 */
export async function deleteDocument(
  messageId: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!messageId || !userId) return { ok: false, error: 'missing arguments' };
  const { data: row, error: readErr } = await supabase
    .from('ai_messages')
    .select('parts, user_id')
    .eq('id', messageId)
    .maybeSingle();
  if (readErr) {
    console.error('[DocumentsService.deleteDocument] read failed', readErr);
    return { ok: false, error: readErr.message };
  }
  if (!row) return { ok: false, error: 'document not found' };
  const currentParts = Array.isArray(row.parts) ? (row.parts as unknown[]) : [];
  const nextParts = currentParts.map((p) => {
    if (
      p &&
      typeof p === 'object' &&
      (p as { type?: string }).type === 'tool-generate_document' &&
      (p as { state?: string }).state === 'output-available'
    ) {
      return { type: 'tool-generate_document', state: 'deleted' as const };
    }
    return p;
  });
  const { error: writeErr } = await supabase
    .from('ai_messages')
    .update({ parts: nextParts as Json })
    .eq('id', messageId);
  if (writeErr) {
    console.error('[DocumentsService.deleteDocument] write failed', writeErr);
    return { ok: false, error: writeErr.message };
  }
  return { ok: true };
}

/**
 * Update a document's markdown body IN PLACE on its `tool-generate_document`
 * part. Provenance (`originalMarkdown`, `editedAt`) is recorded as sibling
 * fields on the same `output` object rather than a new part or table — see
 * the rationale at the top of the task this shipped under: `branch_ai_session`
 * copies `parts` verbatim, so a separate artifacts table would let a branch
 * silently rewrite the source thread's document, and `validateUIMessages`
 * strips unknown part types on replay. Updating in place sidesteps both.
 *
 * `originalMarkdown` is set only on the FIRST edit and preserved thereafter —
 * a second edit must not overwrite the true original.
 *
 * @param messageId - the ai_messages.id holding the document part
 * @param documentId - the document's own id (output.id), to disambiguate a
 *   row that could in principle hold more than one generate_document part
 * @param markdown - the new markdown body; must be non-empty
 * @param userId - authenticated user id; RLS scopes the write (ai_messages is
 *   USER-scoped, so raw auth.uid() is correct here — do not resolve owner)
 */
export async function updateDocumentMarkdown(
  messageId: string,
  documentId: string,
  markdown: string,
  userId: string,
  expectedMarkdown?: string,
): Promise<{ ok: true } | { ok: false; error: string; code?: 'conflict' }> {
  if (!messageId || !documentId || !userId) return { ok: false, error: 'missing arguments' };
  if (!markdown || !markdown.trim()) return { ok: false, error: 'markdown is empty' };

  // `messageId` is the chat UIMessage id here (the editor surfaces pass it
  // straight through); `save_document_markdown`'s `p_message` param is a
  // uuid, so it must be the resolved `ai_messages.id` PK — never the raw
  // chat id. See `classifyDocumentMessageIdentifier` above.
  const pk = await resolveAiMessagePk(messageId, { userId });
  if (!pk) return { ok: false, error: 'document not found' };

  // Legacy callers can resolve a baseline; editor surfaces pass the exact
  // bytes they opened. The database compares that baseline under a row lock.
  let baseline = expectedMarkdown;
  if (baseline === undefined) {
    const { data: row, error } = await supabase
      .from('ai_messages')
      .select('parts')
      .eq('id', pk)
      .maybeSingle();
    if (error) return { ok: false, error: error.message };
    if (!row) return { ok: false, error: 'document not found' };
    const parts = Array.isArray(row.parts)
      ? (row.parts as Array<{
          type?: string;
          state?: string;
          output?: { id?: string; markdown?: string };
        }>)
      : [];
    const matching = parts.filter(
      (part) =>
        part.type === 'tool-generate_document' &&
        part.state === 'output-available' &&
        part.output?.id === documentId,
    );
    if (matching.length !== 1 || typeof matching[0].output?.markdown !== 'string')
      return { ok: false, error: 'document part not found' };
    baseline = matching[0].output.markdown;
  }
  if (markdown === baseline) return { ok: true };
  const { data, error } = await supabase.rpc('save_document_markdown', {
    p_message: pk,
    p_document: documentId,
    p_markdown: markdown,
    p_expected_markdown: baseline,
  });
  if (error) return { ok: false, error: error.message };
  const result = data as { ok?: boolean; error?: string; code?: string } | null;
  return result?.ok === true
    ? { ok: true }
    : {
        ok: false,
        error: result?.error || 'The article could not be saved.',
        ...(result?.code === 'conflict' ? { code: 'conflict' as const } : {}),
      };
}

/**
 * Sets metaTitle and/or metaDescription directly — the one thing
 * `updateDocumentMarkdown` above cannot do. Before this RPC existed, a
 * document whose writing call left `metaDescription` blank (the plain-
 * Markdown fallback path never set it; see `generation-core.ts`) had no way
 * to fix that short of a full "Improve article" rewrite of the whole piece
 * for one missing field.
 *
 * @param messageId - the ai_messages.id holding the document part
 * @param documentId - the document's own id (output.id)
 * @param userId - authenticated user id; RLS scopes the write (ai_messages is
 *   USER-scoped, so raw auth.uid() is correct here — do not resolve owner)
 */
export async function updateDocumentWebMetadata(
  messageId: string,
  documentId: string,
  userId: string,
  fields: { metaTitle?: string; metaDescription?: string },
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!messageId || !documentId || !userId) return { ok: false, error: 'missing arguments' };
  if (fields.metaTitle === undefined && fields.metaDescription === undefined)
    return { ok: false, error: 'Supply at least one of meta title or meta description.' };
  const { data, error } = await supabase.rpc('update_document_web_metadata', {
    p_message: messageId,
    p_document: documentId,
    p_meta_title: fields.metaTitle ?? null,
    p_meta_description: fields.metaDescription ?? null,
  });
  if (error) return { ok: false, error: error.message };
  const result = data as { ok?: boolean; error?: string } | null;
  return result?.ok === true
    ? { ok: true }
    : { ok: false, error: result?.error || 'Could not save' };
}

/**
 * Records a human decision against one quoted editorial finding on this exact
 * article revision. It deliberately leaves the article, web checks and the
 * automated critique untouched: "reviewed" means a person made the call, not
 * that the model's opinion vanished.
 */
export async function setDocumentEditorialReviewDecision(
  messageId: string,
  documentId: string,
  userId: string,
  contentHash: string,
  findingKey: string,
  reviewed: boolean,
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!messageId || !documentId || !userId || !contentHash || !findingKey)
    return { ok: false, error: 'Missing article review details.' };
  const { data, error } = await supabase.rpc('set_document_editorial_review_decision', {
    p_message: messageId,
    p_document: documentId,
    p_content_hash: contentHash,
    p_finding_key: findingKey,
    p_reviewed: reviewed,
  });
  if (error) return { ok: false, error: error.message };
  const result = data as { ok?: boolean; error?: string } | null;
  return result?.ok === true
    ? { ok: true }
    : { ok: false, error: result?.error || 'Could not save this review decision.' };
}

/**
 * An authoritative exact-output read for both editors; no first-document guess.
 *
 * @param messageId - a chat UIMessage id (`ai_messages.message_id`, or its
 *   `msg-`/`usr-` reload fallback) — resolved to the `ai_messages.id` PK
 *   before the read. See `classifyDocumentMessageIdentifier` above.
 */
export async function getDocumentOutput(
  messageId: string,
  documentId: string,
  actorId: string,
): Promise<{ id: string; messageId: string; markdown: string; editedAt: string | null } | null> {
  const pk = await resolveAiMessagePk(messageId, { userId: actorId, excludeDeleted: true });
  if (!pk) return null;
  const { data, error } = await supabase
    .from('ai_messages')
    .select('parts')
    .eq('id', pk)
    .eq('user_id', actorId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const parts = Array.isArray(data?.parts)
    ? (data.parts as Array<{ type?: string; state?: string; output?: Record<string, unknown> }>)
    : [];
  const matches = parts.filter(
    (part) =>
      part.type === 'tool-generate_document' &&
      part.state === 'output-available' &&
      part.output?.id === documentId,
  );
  if (matches.length !== 1 || typeof matches[0].output?.markdown !== 'string') return null;
  const output = matches[0].output;
  return {
    id: documentId,
    messageId: pk,
    markdown: output.markdown as string,
    editedAt: typeof output.editedAt === 'string' ? output.editedAt : null,
  };
}

export const documentsService = {
  listDocuments,
  getDocument,
  deleteDocument,
  updateDocumentMarkdown,
  updateDocumentWebMetadata,
  setDocumentEditorialReviewDecision,
  getDocumentOutput,
};
